import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchFraudReport,
  requestFraudReport,
  retryFailedFraudReport,
  restoreFraudReport,
  resolveFraudReport,
} from 'candidate_profile_redesign/shared/api/fraud_report';
import { updateLastSeenFraudReport } from 'candidate_profile_redesign/shared/api/security_screening';
import { Person } from 'talent_matching/types';
import { useTalentMatchingTabContext } from 'talent_matching/contexts/talent_matching_tab_context';
import { useUpdatePerson } from 'talent_matching/hooks/use_people';

export const useFraudReport = (application: Person) => {
  const { canViewFraudReports } = useTalentMatchingTabContext();
  const { fraudReportPath } = application?.paths || {};
  const updatePerson = useUpdatePerson();

  return useQuery({
    queryKey: ['fraudReport', application.id],
    queryFn: () => (fraudReportPath ? fetchFraudReport(fraudReportPath) : null),
    staleTime: 2000,
    retry: false,
    enabled: canViewFraudReports && !!fraudReportPath,
    refetchInterval: (query) => {
      const fraudReport = query.state.data;

      // Refetch every 5 seconds if the report is pending
      return fraudReport?.status === 'pending' ? 5000 : false;
    },
    select: (fraudReport) => {
      // Update the application's fraudReportHighRisk when fraud report data changes
      if (
        fraudReport &&
        fraudReport.high_risk !== application.fraudReportHighRisk
      ) {
        updatePerson({
          ...application,
          fraudReportHighRisk: fraudReport.high_risk,
        });
      }
      return fraudReport;
    },
  });
};

const useUpdateLastSeenFraudReport = (application: Person) => {
  const { updateLastSeenFraudReportPath } = application?.paths || {};
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      updateLastSeenFraudReport(updateLastSeenFraudReportPath || ''),
    onSuccess: (lastSeenFraudReport) => {
      const person = queryClient.getQueryData<Person>([
        'person',
        application.id,
      ]);
      queryClient.setQueryData(['person', application.id], {
        ...person,
        lastSeenFraudReport: lastSeenFraudReport,
      });
    },
  });
};

const useRestoreFraudReport = (application: Person) => {
  const { restoreFraudReportPath } = application?.paths || {};
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => restoreFraudReport(restoreFraudReportPath || ''),
    onSuccess: (fraudReport) => {
      queryClient.setQueryData(['fraudReport', application.id], fraudReport);
    },
  });
};

const useResolveFraudReport = (application: Person) => {
  const { resolveFraudReportPath } = application?.paths || {};
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      resolved_reason_ids,
      other_text,
    }: {
      resolved_reason_ids: number[];
      other_text?: string | null;
    }) =>
      resolveFraudReport(
        resolveFraudReportPath || '',
        resolved_reason_ids,
        other_text
      ),
    onSuccess: (fraudReport) => {
      queryClient.setQueryData(['fraudReport', application.id], fraudReport);
    },
  });
};

const useRequestFraudReport = (application: Person) => {
  const { fraudReportPath } = application?.paths || {};
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => requestFraudReport(fraudReportPath || ''),
    onSuccess: (fraudReport) => {
      // Sets query data to be a 'pending' fraud report
      queryClient.setQueryData(['fraudReport', application.id], fraudReport);
    },
  });
};

// application_fraud_report_path(application_id: application)

const useRetryFailedFraudReport = (application: Person) => {
  const { fraudReportPath } = application?.paths || {};
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => retryFailedFraudReport(fraudReportPath || ''),
    onSuccess: (fraudReport) => {
      // Sets query data to be a 'pending' fraud report
      queryClient.setQueryData(['fraudReport', application.id], fraudReport);
    },
  });
};

export const useFraudReportActions = (application: Person) => {
  const { mutate: request } = useRequestFraudReport(application);
  const { mutate: retryFailed } = useRetryFailedFraudReport(application);
  const { mutate: updateLastSeen } = useUpdateLastSeenFraudReport(application);
  const { mutate: restore } = useRestoreFraudReport(application);
  const { mutate: resolve } = useResolveFraudReport(application);

  return {
    request,
    retryFailed,
    updateLastSeen,
    restore,
    resolve,
  };
};
